/**
 * Light, dark or the device's colours. styles.css follows `prefers-color-scheme` unless <html> has
 * `data-theme="light"` or `"dark"`. The choice lives in this browser (localStorage, so the first
 * paint is right before anyone is signed in) and, for accounts, on the account (clients.theme,
 * staff.preferences.theme), which wins once bootstrap says who is signed in.
 */
export const THEMES = ['system', 'light', 'dark'];
const KEY = 'ob_theme';

export const normalTheme = (value) => (value === 'light' || value === 'dark' ? value : 'system');

/** The theme stored in this browser; 'system' when there is none or storage is blocked. */
export function readTheme(storage = globalThis.localStorage) {
  try {
    return normalTheme(storage?.getItem(KEY));
  } catch {
    return 'system';
  }
}

export function storeTheme(theme, storage = globalThis.localStorage) {
  try {
    if (normalTheme(theme) === 'system') storage?.removeItem(KEY);
    else storage?.setItem(KEY, theme);
  } catch {
    // private window or blocked storage: the account (if any) still remembers it
  }
}

/** Puts the theme on <html>; 'system' removes the attribute so the media query decides. */
export function applyTheme(theme, root = globalThis.document?.documentElement) {
  if (!root) return;
  const value = normalTheme(theme);
  if (value === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', value);
}

/**
 * Whose account holds the theme on this side of the app: the client's in the client area (not a
 * contact's: they choose per browser), the staff member's in the admin area and while they view the
 * client area as a client. null: only this browser.
 */
export function themeOwner(me, realm = 'client') {
  if (!me) return null;
  if (realm === 'staff' || me.impersonating) return me.staff ? 'staff' : null;
  if (me.client) return me.client.contact ? null : 'client';
  return me.staff ? 'staff' : null;
}

/**
 * The account's choice from bootstrap's `me` ('light' or 'dark'), or null when the browser decides:
 * no account, or none saved (system is stored as null, so the browser's last choice stays).
 */
export function accountTheme(me, realm = 'client') {
  const owner = themeOwner(me, realm);
  const saved = owner === 'client' ? me.client.theme : owner === 'staff' ? me.staff.preferences?.theme : null;
  return saved === 'light' || saved === 'dark' ? saved : null;
}

/** Where to save a change for that account (PATCH { theme }). */
export const THEME_ENDPOINTS = { client: '/api/client/theme', staff: '/api/admin/account/theme' };
